import i18next from "i18next";

import { COMMON_WEB_PATH_JP } from "../constant";
import { REPLACE_AIR } from "../replace_str_constant";

const BASE_DIR = "game-icon-ds88";

/* 每一組內的副檔名嘗試順序。猜錯只是多一次 404。

   ⚠️ 本站兩種都有人用,所以做成可覆寫的參數:
     · 遊戲卡 / 確認彈窗的主圖 → gif 優先(既有行為,動圖先)
     · 排行榜 / 搜尋 / 背景圖   → webp 優先(既有行為,靜圖先)
   收斂成這支的目的是「每一處都有完整 fallback」,不是強迫統一成同一種偏好 ——
   硬統一會讓排行榜與搜尋結果突然全部變成動圖(而且流量變重)。
   日後若確定要統一,把呼叫端的 exts 參數拿掉即可。 */
export const EXTS_ANIMATED_FIRST = ["gif", "webp"];
export const EXTS_STATIC_FIRST = ["webp", "gif"];

/**
 * 遊戲圖片來源解析。
 *
 * 為什麼要抽成 function:
 * 原本這套規則散在七個地方(GameItem、TopGameBoard ×2、GameConfirm ×3、search),
 * 各寫各的,而且已經漂移 —— 有三處**完全沒有 fallback**,當前語系缺圖就直接破圖,
 * 不會退到 en。現在只有這裡一份候選清單,七邊都吃它。
 *
 * ── 檔名 ──
 * 用 REPLACE_AIR(把空白**刪掉**),與傳統站既有資產一致。
 * 注意 cli-integrated-api 走的是空白換底線 `_`,兩邊資產命名本來就不同;
 * 現有圖片都是靠刪空白版命名載到的,所以這裡刻意不跟 SDK 靠齊。
 *
 * ── 語系優先於格式 ──
 * 先把當前語系的兩種副檔名都試完,才退到 `en/`。
 * 圖上通常有在地化文字,寧可拿同語系的另一種副檔名,也不要太早掉到英文版。
 *
 * ── fallback 順序(lang=tw、gif 優先為例)──
 *   1. tw/{platform}/{code}.gif
 *   2. tw/{platform}/{code}.webp
 *   3. en/{platform}/{code}.gif
 *   4. en/{platform}/{code}.webp
 *
 * 預設語系來自 i18next,本站 `REACT_APP_DEFAULT_LANGUAGE = "tw"`。
 *
 * @param {object}   p
 * @param {string}   p.platform  遊戲商代碼,例如 "WM"
 * @param {string}   p.gameCode  後端給的 gameCode(可能含空白)
 * @param {string}   [p.lang]    語系,預設取 i18next.language
 * @param {string[]} [p.exts]    副檔名優先序,預設 gif 先
 * @param {boolean}  [p.longImage] 是否優先取 {code}_long.*(整組缺才退回方圖)
 * @returns {string[]} 由優先到備援的完整 URL 清單
 */
export const buildGameImageCandidates = ({
  platform,
  gameCode,
  lang = i18next.language,
  exts = EXTS_ANIMATED_FIRST,
  longImage = false,
} = {}) => {
  const code = REPLACE_AIR(gameCode);
  if (!platform || !code) return [];

  const base = `${COMMON_WEB_PATH_JP}/${BASE_DIR}`;
  const at = (folder, ext, suffix = "") =>
    `${base}/${folder}/${platform}/${code}${suffix}.${ext}`;

  /* lang 可能就是 en,或是拿不到值(i18next 還沒 init)。
     兩種情況下語系那組會跟 en 那組完全一樣,去重免得白白多打一次 404。 */
  const folders = lang && lang !== "en" ? [lang, "en"] : ["en"];

  /* 每個語系資料夾內把副檔名都試過,再換下一個語系 */
  const group = (suffix) =>
    folders.flatMap((folder) => exts.map((ext) => at(folder, ext, suffix)));

  const square = group("");
  if (!longImage) return square;

  /* 長圖整組(語系 × 副檔名)都缺,才退回一般方圖 ——
     長版位顯示方圖會比較空,但比破圖好。
     長圖資產覆蓋率本來就低,這個退路一定要留。 */
  return [...group("_long"), ...square];
};

/**
 * 給 <img onError> 用:回傳下一個該試的 URL,沒有了就回 null。
 *
 * 用「目前 src 在清單中的位置 + 1」來推進,而不是比對副檔名或路徑尾巴。
 * 舊寫法是一串 endsWith,新增一種路徑就得再補一個 else if,漏補就整條斷掉 ——
 * 本站那三處沒有 fallback 的地方正是這樣來的。
 *
 * img.src 讀出來一定是絕對路徑,候選本來就是絕對路徑,可以直接比對;
 * 保險起見找不到時當作「還沒開始」,從第二個候選接下去。
 *
 * @param {string}   currentSrc  e.target.src
 * @param {string[]} candidates  buildGameImageCandidates() 的結果
 * @returns {string|null}
 */
export const nextGameImageSrc = (currentSrc, candidates = []) => {
  if (!candidates.length) return null;
  const idx = candidates.indexOf(currentSrc);
  const next = idx === -1 ? 1 : idx + 1;
  return next < candidates.length ? candidates[next] : null;
};

/**
 * 掛在 <img onError> 上的共用 handler 產生器。
 *
 * 用法:
 *   const candidates = buildGameImageCandidates({ platform, gameCode });
 *   <img src={candidates[0]} onError={handleGameImageError(candidates)} />
 *
 * 全部試完就把 onerror 解掉,避免瀏覽器對同一個壞網址反覆觸發。
 */
export const handleGameImageError = (candidates) => (event) => {
  const img = event.target;
  const next = nextGameImageSrc(img.src, candidates);
  if (next) {
    img.src = next;
  } else {
    img.onerror = null;
  }
};

export default buildGameImageCandidates;
